import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, clientAuth } from "@/lib/api";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, ArrowRight, CheckCircle2 } from "lucide-react";

export default function ClientLogin() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const requestCode = async () => {
    setStatusMsg("");
    if (!email) return toast.error("Please enter your email");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/client/request-passcode", { email });
      setStage("code");
      setDevCode(data.passcode_dev || "");
      setStatusMsg(data.message || "If we found a record for this email, a 6-digit passcode has been sent.");
      toast.success("Check your email for a 6-digit passcode");
    } catch (e) {
      // Generic error — do not expose backend details
      toast.error("We couldn't send your passcode right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!code || code.length < 6) return toast.error("Please enter the full 6-digit passcode");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/client/verify-passcode", { email, passcode: code });
      clientAuth.setSession(data.token, data.client_id, data.name, email);
      toast.success(`Welcome back, ${data.name}`);
      navigate("/client/dashboard");
    } catch (e) {
      toast.error("That passcode didn't work. Please check the code and try again, or request a new one.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md kcis-card p-8 sm:p-10 kcis-fade-up">
          <div className="h-12 w-12 rounded-2xl bg-purple-50 text-purple-900 flex items-center justify-center mb-5">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="kcis-overline text-purple-900">Client portal</div>
          <h1 className="font-display text-3xl tracking-tight text-slate-900 mt-2">
            {stage === "email" ? "Sign in with your email" : "Enter your passcode"}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {stage === "email"
              ? "We'll email you a 6-digit passcode to access your wellness dashboard."
              : `We sent a 6-digit code to ${email}. It expires in 15 minutes.`}
          </p>

          {stage === "email" ? (
            <div className="mt-7 space-y-4">
              <div>
                <Label className="text-sm text-slate-700">Email</Label>
                <Input
                  data-testid="client-login-email"
                  type="email"
                  autoComplete="email"
                  className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <Button onClick={requestCode} disabled={loading} className="w-full rounded-full bg-purple-900 hover:bg-purple-800 text-white h-12" data-testid="client-login-send-code">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Send Passcode <ArrowRight className="h-4 w-4 ml-2" /></>)}
              </Button>
            </div>
          ) : (
            <div className="mt-7 space-y-4">
              {statusMsg && (
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 flex items-start gap-2 text-sm text-emerald-900">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{statusMsg}</span>
                </div>
              )}
              <div>
                <Label className="text-sm text-slate-700">6-digit passcode</Label>
                <Input
                  data-testid="client-login-code"
                  className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12 text-lg tracking-[0.4em] font-mono"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
                {devCode && (
                  <p className="text-xs text-slate-500 mt-2">
                    Dev mode passcode: <span className="font-mono font-medium text-purple-900">{devCode}</span>
                  </p>
                )}
              </div>
              <Button onClick={verify} disabled={loading} className="w-full rounded-full bg-purple-900 hover:bg-purple-800 text-white h-12" data-testid="client-login-verify">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button onClick={() => { setStage("email"); setCode(""); setDevCode(""); setStatusMsg(""); }} className="text-slate-500 hover:text-purple-900">
                  ← Use a different email
                </button>
                <button onClick={requestCode} disabled={loading} className="text-purple-900 hover:underline disabled:opacity-50">
                  Resend code
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
